import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { defineMessages, injectIntl } from 'react-intl';

import Button from '../../Atoms/Button';
import Loader from '../../Atoms/Loader';
import CreditCardForm from '../../Molecules/CreditCardForm';
import PixForm from '../../Molecules/PixForm';

import './index.sass';

import { doPaymentCheckout, doPaymentPixCheckout, getPayment, changePaymentMethod } from '../../../store/actions';
import { Analytics } from '../../../shared';

/* global pagarme */
class PayForm extends React.Component {
	state = {
		method: !localStorage.getItem('locale') || localStorage.getItem('locale') === 'pt' ? 'pix' : 'creditCard',
		pagarmeLoading: false,
		finished: false
	};

	componentDidUpdate() {
		const { finished } = this.state;
		const { payment, onPaid } = this.props;
		const { loading, status } = payment;

		if (!finished && !loading && status === 'PAID') {
			this.setState({ finished: true });
			onPaid();
		}
	}

	onPayByCreditCard = async (e, card) => {
		e.preventDefault();
		const self = this;
		const { orderId, organization: orgState, doPaymentCheckout } = this.props;
		const { card_number, card_holder_name, card_expiration_month, card_expiration_year, card_cvv } = card;
		const { organization } = orgState;
		const { name: orgName } = organization;

		this.setState({ pagarmeLoading: true });

		try {
			// Call Pagar.me token creation API instead of encryption
			const tokenResponse = await this.createPagarMeToken(card);
			
			self.setState({ pagarmeLoading: false });
			// Send token ID as card_hash to backend
			doPaymentCheckout(orderId, tokenResponse.id);
			Analytics.sendEvent('paid', 'buying', orgName);

		} catch (error) {
			console.error('Token creation error:', error);
			self.setState({ pagarmeLoading: false });
			// Handle token creation failure
		}

		Analytics.sendEvent('paying', 'buying', orgName);
	}

	/**
	 * Create a Pagar.me token using the token creation API
	 * @param {Object} card - Card data object
	 * @returns {Promise<Object>} Token response from Pagar.me
	 */
	createPagarMeToken = async (card) => {
		const { card_number, card_holder_name, card_expiration_month, card_expiration_year, card_cvv } = card;
		
		// Prepare card data for token creation API
		const cardData = {
			type: 'card',
			card: {
				number: card_number,
				holder_name: card_holder_name,
				exp_month: parseInt(card_expiration_month),
				exp_year: parseInt(card_expiration_year),
				cvv: card_cvv
			}
		};

		// Get public key from environment
		const publicKey = process.env.REACT_APP_PAGARME_PUBLIC_KEY;
		
		if (!publicKey) {
			throw new Error('Pagar.me public key not configured');
		}

		try {
			const response = await fetch(`https://api.pagar.me/core/v5/tokens?appId=${publicKey}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(cardData)
			});

			if (!response.ok) {
				let errorMessage = `Token creation failed: ${response.status} ${response.statusText}`;
				try {
					const errorData = await response.json();
					errorMessage = errorData.message || errorData.error || errorMessage;
				} catch (parseError) {
					console.warn('Could not parse error response:', parseError);
				}
				throw new Error(errorMessage);
			}

			const tokenData = await response.json();
			return tokenData;

		} catch (error) {
			console.error('Error creating Pagar.me token:', error);
			throw error;
		}
	}

	onPayByPix = () => {
		const { orderId, organization: orgState, doPaymentPixCheckout } = this.props;
		const { organization } = orgState;
		const { name: orgName } = organization;
		doPaymentPixCheckout(orderId);
		Analytics.sendEvent('paid', 'buying', orgName);
	}

	render () {
		const { orderId, payment, order, onCancel, intl } = this.props;
		const { method, pagarmeLoading } = this.state;
		const { loading: loadingPayment, error, pixText } = payment;
		const { loading: loadingOrder } = order;
		const { formatMessage } = intl;
		const messages = defineMessages({
			payByCreditCard: {
				id: 'payForm.payByCreditCard',
				defaultMessage: 'Pagar com cartão de crédito'
			},
			payByPix: {
				id: 'payForm.payByPix',
				defaultMessage: 'Pagar com PIX'
			},
			pay: {
				id: 'payForm.pay',
				defaultMessage: 'Pagar'
			},
			changeAddress: {
				id: 'payForm.changeAddress',
				defaultMessage: 'Alterar endereço'
			}
		});

		if (pagarmeLoading || loadingOrder) {
			return (
				<div className="PayForm__loader">
					<Loader />
				</div>
			)
		}

		return (
			<>
				{method === 'creditCard' ? (
					<CreditCardForm
						error={error}
						loading={pagarmeLoading || loadingPayment || loadingOrder}
						onPay={this.onPayByCreditCard}
					/>
				) : (
					<PixForm
						orderId={orderId}
						pixText={pixText}
						error={error}
						loading={pagarmeLoading || loadingPayment || loadingOrder}
						onPay={this.onPayByPix}
					/>
				)}
				{!pagarmeLoading && !loadingOrder && (
					<>
						{(!localStorage.getItem('locale') || localStorage.getItem('locale') === 'pt') && (
							<Button
								className="PayForm__change-method"
								color="secondary"
								isLink
								onClick={() => {
									this.setState((prevState) => ({ method: prevState.method === 'pix' ? 'creditCard' : 'pix' }));
									this.props.changePaymentMethod(this.state.method === 'pix' ? 'creditCard' : 'pix');
									}
								}
								size="medium"
							>
								{method === 'pix' ? (
									formatMessage(messages.payByCreditCard)
								) : (
									formatMessage(messages.payByPix)
								)}
							</Button>
						)}
						<Button
							className="PayForm__cancel"
							color="primary"
							isLink
							onClick={onCancel}
							size="medium"
						>
							{formatMessage(messages.changeAddress)}
						</Button>
					</>
				)}
			</>
		)
	}
}

PayForm.propTypes = {
	orderId: PropTypes.string.isRequired,
	onPaid: PropTypes.func.isRequired,
	onError: PropTypes.func,
	onEditCustomer: PropTypes.func,
	onCancel: PropTypes.func,
	intl: PropTypes.shape().isRequired
};

PayForm.propTypes = {
	onError: () => {},
	onEditCustomer: () => {},
	onCancel: () => {}
}

const mapStateToProps = state => ({
	payment: state.payment,
	order: state.order,
	organization: state.organization,
	method: state.payment.method
});

const mapActionsToProps = dispatch => ({
	getPayment: (orderId) => dispatch(getPayment(orderId)),
	doPaymentCheckout: (orderId, cardHash, tokenData) => dispatch(doPaymentCheckout(orderId, cardHash)),
	doPaymentPixCheckout: (orderId) => dispatch(doPaymentPixCheckout(orderId)),
	changePaymentMethod: (method) => dispatch(changePaymentMethod(method))
});

export default connect(mapStateToProps, mapActionsToProps)(injectIntl(PayForm));
